// LE COMPTE RENDU DU BACK-OFFICE, en phrases — ce que le volet d'un message confirmé dit de son intégration.
// ⚠️ 🔴 « CONFIRMÉ » NE VEUT PAS DIRE « TOUT EST ENTRÉ » : le back-office écarte un article sans code-barres, une ligne de
//    bon sans code, une taille qu'aucune grille ne connaît. L'EDI jetait ce compte rendu et affichait « Rien à faire »
//    sur un catalogue entièrement écarté. Les champs sont ceux que la route d'ingest du back-office rend (`data`).

/** Le compte rendu tel que le serveur l'a gardé — `null` pour un message confirmé avant qu'on le garde. */
export type BoReceipt = Record<string, unknown> | null | undefined;

const count = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0);
const list = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

/** Ce que le back-office a écarté ou n'a pas pu placer — vide s'il n'a rien écarté. */
export function receiptGaps(r: BoReceipt): string[] {
  if (!r) return [];
  const gaps: string[] = [];
  const skippedItems = count(r.skippedItems);
  if (skippedItems) gaps.push(plural(skippedItems, 'article sans code-barres écarté', 'articles sans code-barres écartés'));
  const unknownSizes = list(r.sizesUnknown);
  if (unknownSizes.length) gaps.push(`${plural(unknownSizes.length, 'taille inconnue', 'tailles inconnues')} des grilles (${unknownSizes.slice(0, 5).join(', ')}${unknownSizes.length > 5 ? '…' : ''})`);
  const ambiguousSizes = list(r.sizesAmbiguous);
  if (ambiguousSizes.length) gaps.push(`${plural(ambiguousSizes.length, 'taille', 'tailles')} présente${ambiguousSizes.length > 1 ? 's' : ''} dans plusieurs grilles (${ambiguousSizes.slice(0, 5).join(', ')}${ambiguousSizes.length > 5 ? '…' : ''})`);
  const skippedLines = count(r.skippedLines);
  if (skippedLines) gaps.push(plural(skippedLines, 'ligne sans code-barres écartée', 'lignes sans code-barres écartées'));
  const withoutQty = count(r.linesWithoutQty);
  if (withoutQty) gaps.push(plural(withoutQty, 'ligne sans quantité', 'lignes sans quantité'));
  const merged = count(r.mergedLines);
  if (merged) gaps.push(plural(merged, 'ligne du même article additionnée', 'lignes du même article additionnées'));
  const ambiguousOrders = list(r.ambiguousOrderLines);
  if (ambiguousOrders.length) gaps.push(plural(ambiguousOrders.length, 'code annoncé pour deux commandes', 'codes annoncés pour deux commandes'));
  return gaps;
}

/** Ce que le back-office a intégré, en une phrase — `null` quand le compte rendu ne le dit pas. */
export function receiptSummary(r: BoReceipt): string | null {
  if (!r) return null;
  if (r.deduped === true) return 'déjà reçu par le back-office : rien n’a été réécrit';
  const parts: string[] = [];
  if (typeof r.articles === 'number') {
    parts.push(`${plural(r.articles, 'article écrit', 'articles écrits')} (${count(r.articlesCreated)} créé${count(r.articlesCreated) > 1 ? 's' : ''}, ${count(r.articlesUpdated)} mis à jour)`);
  }
  const restored = count(r.restoredModels);
  if (restored) parts.push(plural(restored, 'modèle remis en service', 'modèles remis en service'));
  if (typeof r.deliveryNote === 'string') {
    parts.push(`bon n° ${r.deliveryNote}${typeof r.deliveryNoteLines === 'number' ? `, ${plural(r.deliveryNoteLines, 'ligne', 'lignes')}` : ''}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

/**
 * La conclusion du volet pour un message CONFIRMÉ : les écarts d'abord (ton d'avertissement), sinon ce qui est entré.
 * ⚠️ Un message confirmé avant qu'on garde le compte rendu n'en a pas : on ne dit alors que ce qu'on sait.
 */
export function confirmedConclusion(r: BoReceipt): { tone: 'ok' | 'warn'; text: string } {
  const gaps = receiptGaps(r);
  if (gaps.length) return { tone: 'warn', text: `Intégré par le back-office, avec des écarts : ${gaps.join(' ; ')}.` };
  const summary = receiptSummary(r);
  return { tone: 'ok', text: summary ? `Intégré par le back-office : ${summary}.` : 'Rien à faire — le message a suivi son cours normalement.' };
}
